import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, Download, Calendar, AlertTriangle, Clock } from "lucide-react";

/** Format a number of seconds as HH:MM (hours+minutes; seconds rounded off for reports). */
function hm(secs) {
  secs = Math.max(0, Math.floor(secs || 0));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Local YYYY-MM-DD (no time, no TZ shift). */
function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** This week's Friday (or upcoming Friday if today is Mon-Thu). */
function thisFriday() {
  const d = new Date();
  const dow = d.getDay(); // Sun=0 .. Sat=6  -> map to Mon=0..Sun=6
  const mondayIdx = (dow + 6) % 7; // Mon=0..Sun=6
  const delta = (4 - mondayIdx + 7) % 7; // days forward to Friday
  d.setDate(d.getDate() + delta);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function thisMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const PERIODS = [
  { key: "daily", label: "Daily" },
  { key: "weekly", label: "Weekly (Mon–Fri)" },
  { key: "monthly", label: "Monthly" },
];

export default function ShiftReports() {
  const [period, setPeriod] = useState("daily");
  const [date, setDate] = useState(todayISO());
  const [weekEnding, setWeekEnding] = useState(thisFriday());
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState({ rows: [], period: "", start: "", end: "" });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url = "/shifts/report/daily";
      const params = {};
      if (period === "daily") { url = "/shifts/report/daily"; params.date = date; }
      if (period === "weekly") { url = "/shifts/report/weekly"; params.week_ending = weekEnding; }
      if (period === "monthly") { url = "/shifts/report/monthly"; params.month = month; }
      const { data: resp } = await api.get(url, { params });
      setData(resp);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [period, date, weekEnding, month]);

  useEffect(() => { load(); }, [load]);

  const exportCSV = () => {
    const headers = [
      "Name", "Email", "Role",
      "Shifts", "Shift hrs", "Active hrs", "Idle hrs", "Break hrs",
      "Leads submitted", "Leads/hr", "Late logins", "Break overruns",
      "Break breakdown",
    ];
    const lines = [headers.join(",")];
    for (const r of data.rows || []) {
      const reasons = Object.entries(r.break_reasons || {})
        .map(([k, v]) => `${k}=${hm(v)}`)
        .join("; ");
      const row = [
        r.name, r.email, r.role,
        r.shifts_count,
        hm(r.total_shift_seconds), hm(r.active_seconds), hm(r.idle_seconds), hm(r.break_seconds),
        r.leads_submitted, r.leads_per_hour,
        r.late_logins, r.break_overruns,
        reasons,
      ].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`);
      lines.push(row.join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `shift-report-${(data.period || period).replace(/[^a-z0-9]+/gi, "_")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totals = (data.rows || []).reduce(
    (acc, r) => {
      acc.shift += r.total_shift_seconds || 0;
      acc.active += r.active_seconds || 0;
      acc.idle += r.idle_seconds || 0;
      acc.break += r.break_seconds || 0;
      acc.leads += r.leads_submitted || 0;
      acc.late += r.late_logins || 0;
      acc.overrun += r.break_overruns || 0;
      return acc;
    },
    { shift: 0, active: 0, idle: 0, break: 0, leads: 0, late: 0, overrun: 0 },
  );
  const totalLeadsPerHour = totals.active > 0 ? Number((totals.leads / (totals.active / 3600)).toFixed(2)) : 0;

  return (
    <div className="space-y-4" data-testid="shift-reports">
      <div className="flex items-center gap-2 flex-wrap">
        {/* Period switcher */}
        <div className="inline-flex rounded-sm border border-border overflow-hidden" data-testid="report-period-switch">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setPeriod(p.key)}
              data-testid={`report-period-${p.key}`}
              className={`px-3 h-9 text-sm border-r border-border last:border-r-0 ${
                period === p.key ? "bg-primary text-primary-foreground" : "bg-background hover:bg-secondary/60"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Date selector — varies by period */}
        {period === "daily" && (
          <div className="flex items-center gap-1">
            <Calendar size={14} className="text-muted-foreground" />
            <Input
              type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="rounded-sm h-9 w-40 font-mono"
              data-testid="report-date-input"
            />
          </div>
        )}
        {period === "weekly" && (
          <div className="flex items-center gap-1">
            <Calendar size={14} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Week ending (Fri):</span>
            <Input
              type="date" value={weekEnding} onChange={(e) => setWeekEnding(e.target.value)}
              className="rounded-sm h-9 w-40 font-mono"
              data-testid="report-week-ending-input"
            />
          </div>
        )}
        {period === "monthly" && (
          <div className="flex items-center gap-1">
            <Calendar size={14} className="text-muted-foreground" />
            <Input
              type="month" value={month} onChange={(e) => setMonth(e.target.value)}
              className="rounded-sm h-9 w-40 font-mono"
              data-testid="report-month-input"
            />
          </div>
        )}

        <Button onClick={load} variant="outline" className="rounded-sm h-9" data-testid="report-refresh">
          <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <Button onClick={exportCSV} variant="outline" className="rounded-sm h-9" data-testid="report-export">
          <Download size={14} className="mr-1" /> Export CSV
        </Button>

        <div className="text-xs text-muted-foreground ml-2">
          {data.period && <>Range <span className="font-mono">{data.start?.slice(0,10)} → {data.end?.slice(0,10)}</span> · </>}
          {data.rows?.length || 0} users
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
        <SummaryTile label="Total shift" value={hm(totals.shift)} />
        <SummaryTile label="Active" value={hm(totals.active)} tone="emerald" />
        <SummaryTile label="Idle" value={hm(totals.idle)} tone="amber" />
        <SummaryTile label="Break" value={hm(totals.break)} tone="rose" />
        <SummaryTile label="Leads" value={totals.leads} />
        <SummaryTile label="Leads / hr" value={totalLeadsPerHour} tone="emerald" />
        <SummaryTile label="Late · Overrun" value={`${totals.late} · ${totals.overrun}`} tone={totals.late + totals.overrun > 0 ? "amber" : undefined} />
      </div>

      {/* Per-user table */}
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm" data-testid="report-table">
          <thead className="bg-secondary/40">
            <tr className="text-left border-b border-border">
              <th className="px-3 py-2 meta-label">Name</th>
              <th className="px-3 py-2 meta-label">Role</th>
              <th className="px-3 py-2 meta-label">Shifts</th>
              <th className="px-3 py-2 meta-label">Shift</th>
              <th className="px-3 py-2 meta-label">Active</th>
              <th className="px-3 py-2 meta-label">Idle</th>
              <th className="px-3 py-2 meta-label">Break</th>
              <th className="px-3 py-2 meta-label">Leads</th>
              <th className="px-3 py-2 meta-label">Leads/hr</th>
              <th className="px-3 py-2 meta-label">Late</th>
              <th className="px-3 py-2 meta-label">Overrun</th>
              <th className="px-3 py-2 meta-label">Break reasons</th>
            </tr>
          </thead>
          <tbody>
            {(!data.rows || data.rows.length === 0) && (
              <tr><td colSpan={12} className="px-3 py-8 text-center text-muted-foreground">No data in this period.</td></tr>
            )}
            {(data.rows || []).map((r) => (
              <tr key={r.user_id} className="border-t border-border hover:bg-secondary/20" data-testid={`report-row-${r.email}`}>
                <td className="px-3 py-2">
                  <div className="font-medium">{r.name || r.email}</div>
                  <div className="text-[11px] text-muted-foreground">{r.email}</div>
                </td>
                <td className="px-3 py-2">{r.role || "—"}</td>
                <td className="px-3 py-2 font-mono">{r.shifts_count}</td>
                <td className="px-3 py-2 font-mono">{hm(r.total_shift_seconds)}</td>
                <td className="px-3 py-2 font-mono text-emerald-700">{hm(r.active_seconds)}</td>
                <td className="px-3 py-2 font-mono text-amber-700">{hm(r.idle_seconds)}</td>
                <td className="px-3 py-2 font-mono text-rose-700">{hm(r.break_seconds)}</td>
                <td className="px-3 py-2 font-mono">{r.leads_submitted}</td>
                <td className="px-3 py-2 font-mono font-semibold">{r.leads_per_hour}</td>
                <td className="px-3 py-2">
                  {r.late_logins > 0
                    ? <span className="inline-flex items-center gap-1 text-amber-700"><Clock size={12} />{r.late_logins}</span>
                    : <span className="text-muted-foreground">0</span>}
                </td>
                <td className="px-3 py-2">
                  {r.break_overruns > 0
                    ? <span className="inline-flex items-center gap-1 text-rose-700"><AlertTriangle size={12} />{r.break_overruns}</span>
                    : <span className="text-muted-foreground">0</span>}
                </td>
                <td className="px-3 py-2">
                  {Object.keys(r.break_reasons || {}).length === 0
                    ? <span className="text-muted-foreground text-xs">—</span>
                    : (
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(r.break_reasons).map(([k, v]) => (
                          <span key={k} className="text-[11px] bg-secondary/60 border border-border px-1.5 py-0.5 rounded-sm font-mono">
                            {k} {hm(v)}
                          </span>
                        ))}
                      </div>
                    )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, tone }) {
  const toneClass =
    tone === "emerald" ? "text-emerald-700" :
    tone === "amber"   ? "text-amber-700" :
    tone === "rose"    ? "text-rose-700"   : "text-foreground";
  return (
    <div className="border border-border rounded-sm bg-secondary/30 px-3 py-2">
      <div className="meta-label text-[10px]">{label}</div>
      <div className={`font-mono text-lg ${toneClass}`}>{value}</div>
    </div>
  );
}
